(() => {
  const NAV_SELECTOR = '[data-shefa-mobile-bottom-nav]';
  const CATEGORY_SHEET_SELECTOR = '[data-shefa-categories-sheet]';
  const SEARCH_OVERLAY_SELECTOR = '[data-shefa-mobile-predictive-search]';

  const setAccordionState = (button, open) => {
    const id = button.getAttribute('aria-controls');
    if (!id) return;
    const panel = document.getElementById(id);
    if (!panel) return;
    button.setAttribute('aria-expanded', open ? 'true' : 'false');
    panel.classList.toggle('is-open', open);
  };

  const resetAccordions = (sheet) => {
    if (!sheet) return;
    sheet.querySelectorAll('[data-shefa-category-toggle]').forEach((button) => {
      setAccordionState(button, false);
    });
  };

  const initBottomNav = () => {
    const nav = document.querySelector(NAV_SELECTOR);
    const categorySheet = document.querySelector(CATEGORY_SHEET_SELECTOR);
    const searchOverlay = document.querySelector(SEARCH_OVERLAY_SELECTOR);

    if (!nav || !categorySheet || !searchOverlay || nav.dataset.initialized === 'true') return;
    nav.dataset.initialized = 'true';

    const categoriesButton = nav.querySelector('[data-shefa-action="categories"]');
    const searchButtons = Array.from(document.querySelectorAll('[data-shefa-action="search"]'));
    const categoryPanel = categorySheet.querySelector('.shefa-mobile-categories-sheet__panel');
    const searchInput = searchOverlay.querySelector('input[type="search"]');
    const predictiveSearch = searchOverlay.querySelector('predictive-search');
    let lastFocusedElement = null;
    let lastSearchTouchAt = 0;

    const setSearchTriggersExpanded = (expanded) => {
      searchButtons.forEach((button) => {
        button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      });
    };

    if (searchInput) {
      searchInput.setAttribute('inputmode', 'search');
      searchInput.setAttribute('enterkeyhint', 'search');
      searchInput.setAttribute('autocomplete', 'off');
    }

    const clearPredictiveResults = () => {
      if (!predictiveSearch) return;
      if (typeof predictiveSearch.close === 'function') predictiveSearch.close(false);
    };

    const focusSearchInput = () => {
      if (!searchInput) return false;

      try {
        searchInput.focus({ preventScroll: true });
      } catch (error) {
        searchInput.focus();
      }

      try {
        const cursorPosition = searchInput.value.length;
        searchInput.setSelectionRange(cursorPosition, cursorPosition);
      } catch (error) {
        // Some browsers/input types do not expose setSelectionRange.
      }

      return document.activeElement === searchInput;
    };

    let setSearchOpen;

    const setCategorySheetOpen = (open) => {
      if (open) {
        setSearchOpen(false, false);
        lastFocusedElement = document.activeElement;
        resetAccordions(categorySheet);
        categorySheet.classList.add('is-open');
        categorySheet.setAttribute('aria-hidden', 'false');
        categoriesButton?.setAttribute('aria-expanded', 'true');
        nav.classList.add('is-sheet-open');
        document.body.classList.add('shefa-bottom-sheet-open');
        window.requestAnimationFrame(() => categoryPanel?.focus({ preventScroll: true }));
      } else {
        categorySheet.classList.remove('is-open');
        categorySheet.setAttribute('aria-hidden', 'true');
        categoriesButton?.setAttribute('aria-expanded', 'false');
        nav.classList.remove('is-sheet-open');
        document.body.classList.remove('shefa-bottom-sheet-open');
        resetAccordions(categorySheet);
      }
    };

    setSearchOpen = (open, restoreFocus = true, focusImmediately = false) => {
      if (open) {
        setCategorySheetOpen(false);
        lastFocusedElement = document.activeElement;

        // Important for iPhone/iPad Safari: the closed overlay stays rendered
        // (opacity: 0, pointer-events: none). Make it accessible, focus the real
        // input while we are still inside the trusted tap, then reveal it.
        searchOverlay.setAttribute('aria-hidden', 'false');
        setSearchTriggersExpanded(true);
        nav.classList.add('is-search-open');
        document.body.classList.add('shefa-bottom-search-open');

        if (focusImmediately && searchInput) {
          focusSearchInput();
        }

        searchOverlay.classList.add('is-open');

        if (!focusImmediately) {
          window.requestAnimationFrame(focusSearchInput);
        } else if (searchInput && document.activeElement !== searchInput) {
          // Secondary visual fallback only; the synchronous focus above is
          // what is expected to summon the software keyboard.
          window.requestAnimationFrame(focusSearchInput);
        }
      } else {
        if (searchInput && document.activeElement === searchInput) {
          searchInput.blur();
        }
        searchOverlay.classList.remove('is-open');
        searchOverlay.setAttribute('aria-hidden', 'true');
        setSearchTriggersExpanded(false);
        nav.classList.remove('is-search-open');
        document.body.classList.remove('shefa-bottom-search-open');
        clearPredictiveResults();
        if (restoreFocus && lastFocusedElement instanceof HTMLElement) {
          window.requestAnimationFrame(() => lastFocusedElement.focus({ preventScroll: true }));
        }
      }
    };

    const openSearchForTyping = () => {
      setSearchOpen(true, false, true);
    };

    categoriesButton?.addEventListener('click', (event) => {
      event.preventDefault();
      setCategorySheetOpen(!categorySheet.classList.contains('is-open'));
    });

    searchButtons.forEach((searchButton) => {
      // Open on touchstart, not after the overlay animation. This keeps the
      // focus call inside the exact physical gesture that Safari trusts for
      // showing the software keyboard. Both the bottom-nav button and the
      // header magnifier trigger this exact same path.
      searchButton.addEventListener('touchstart', () => {
        lastSearchTouchAt = Date.now();
        openSearchForTyping();
      }, { passive: true });

      // Mouse/trackpad/keyboard fallback, plus Android browsers that dispatch
      // click without a touch event. Ignore the synthetic click after touch.
      searchButton.addEventListener('click', (event) => {
        event.preventDefault();
        if (Date.now() - lastSearchTouchAt < 700) return;
        openSearchForTyping();
      });
    });

    categorySheet.querySelectorAll('[data-shefa-sheet-close]').forEach((button) => {
      button.addEventListener('click', () => setCategorySheetOpen(false));
    });

    categorySheet.querySelectorAll('[data-shefa-category-toggle]').forEach((button) => {
      button.addEventListener('click', () => {
        setAccordionState(button, button.getAttribute('aria-expanded') !== 'true');
      });
    });

    categorySheet.addEventListener('click', (event) => {
      if (event.target.closest('a[href]')) setCategorySheetOpen(false);
    });

    searchOverlay.querySelectorAll('[data-shefa-search-close]').forEach((button) => {
      button.addEventListener('click', () => setSearchOpen(false));
    });

    searchOverlay.addEventListener('click', (event) => {
      if (event.target.closest('a[href]')) setSearchOpen(false, false);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      if (searchOverlay.classList.contains('is-open')) {
        setSearchOpen(false);
      } else if (categorySheet.classList.contains('is-open')) {
        setCategorySheetOpen(false);
      }
    });

    nav.querySelectorAll('a:not([data-shefa-action="categories"])').forEach((link) => {
      link.addEventListener('click', () => {
        if (categorySheet.classList.contains('is-open')) setCategorySheetOpen(false);
        if (searchOverlay.classList.contains('is-open')) setSearchOpen(false, false);
      });
    });

    const syncScrolledState = () => {
      nav.classList.toggle('is-scrolled', window.scrollY > 12);
    };

    syncScrolledState();
    window.addEventListener('scroll', syncScrolledState, { passive: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initBottomNav, { once: true });
  } else {
    initBottomNav();
  }
})();
